import { midiToFrequency } from '../music/pitch';

/**
 * A small, deterministic Web Audio instrument. No samples are loaded: tones are
 * built from fixed harmonic spectra, so the platform needs no audio assets and
 * every render of the same notes is identical.
 */

export type Timbre = 'piano' | 'harpsichord';

interface Spectrum {
  harmonics: number[];
  attack: number;
  /** Decay time constant in seconds at middle C; lower notes ring longer. */
  decay: number;
  release: number;
  brightness: number;
  gain: number;
}

const SPECTRA: Record<Timbre, Spectrum> = {
  piano: {
    harmonics: [1, 0.42, 0.22, 0.12, 0.08, 0.05, 0.03, 0.02],
    attack: 0.006,
    decay: 1.5,
    release: 0.28,
    brightness: 7,
    gain: 0.19,
  },
  harpsichord: {
    harmonics: [1, 0.75, 0.62, 0.5, 0.42, 0.35, 0.3, 0.24, 0.2, 0.16, 0.12, 0.1],
    attack: 0.002,
    decay: 1.05,
    release: 0.09,
    brightness: 14,
    gain: 0.11,
  },
};

/** Deterministic pseudo-random numbers (mulberry32) for the room impulse response. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Voice {
  stop(at?: number): void;
  startTime: number;
}

export class Synth {
  readonly ctx: AudioContext;
  private master: GainNode;
  private dry: GainNode;
  private wet: GainNode;
  private waves = new Map<Timbre, PeriodicWave>();
  timbre: Timbre = 'piano';

  constructor() {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx({ latencyHint: 'interactive' });
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.ratio.value = 3;
    comp.attack.value = 0.004;
    comp.release.value = 0.2;
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    this.dry = this.ctx.createGain();
    this.wet = this.ctx.createGain();
    this.dry.gain.value = 0.86;
    this.wet.gain.value = 0.2;
    const reverb = this.ctx.createConvolver();
    reverb.buffer = this.impulse(1.9, 7);
    this.dry.connect(comp);
    this.wet.connect(reverb);
    reverb.connect(comp);
    comp.connect(this.master);
    this.master.connect(this.ctx.destination);
  }

  private impulse(seconds: number, seed: number): AudioBuffer {
    const rate = this.ctx.sampleRate;
    const len = Math.floor(rate * seconds);
    const buf = this.ctx.createBuffer(2, len, rate);
    for (let ch = 0; ch < 2; ch++) {
      const rnd = seeded(seed + ch);
      const data = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) data[i] = (rnd() * 2 - 1) * Math.pow(1 - i / len, 3.2);
    }
    return buf;
  }

  private wave(t: Timbre): PeriodicWave {
    let w = this.waves.get(t);
    if (!w) {
      const h = SPECTRA[t].harmonics;
      const real = new Float32Array(h.length + 1);
      const imag = new Float32Array(h.length + 1);
      h.forEach((a, i) => (imag[i + 1] = a));
      w = this.ctx.createPeriodicWave(real, imag);
      this.waves.set(t, w);
    }
    return w;
  }

  async resume(): Promise<void> {
    if (this.ctx.state !== 'running') await this.ctx.resume();
  }

  get now(): number {
    return this.ctx.currentTime;
  }

  setVolume(v: number): void {
    this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.02);
  }

  /** Schedule one note. `dur` is the written duration in seconds. */
  play(midi: number, when: number, dur: number, velocity = 0.8): Voice {
    const spec = SPECTRA[this.timbre];
    const ctx = this.ctx;
    const f = midiToFrequency(midi);
    const t0 = Math.max(when, ctx.currentTime);
    const pitchFactor = Math.pow(2, -(midi - 60) / 24);
    const tau = spec.decay * pitchFactor;
    const level = spec.gain * velocity * (midi < 48 ? 1.15 : midi > 76 ? 0.8 : 1);

    const osc = ctx.createOscillator();
    osc.setPeriodicWave(this.wave(this.timbre));
    osc.frequency.value = f;
    const osc2 = ctx.createOscillator();
    osc2.setPeriodicWave(this.wave(this.timbre));
    osc2.frequency.value = f;
    osc2.detune.value = this.timbre === 'piano' ? 1.6 : 0.8;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.4;
    const bright = Math.min(12000, f * spec.brightness);
    filter.frequency.setValueAtTime(bright, t0);
    filter.frequency.setTargetAtTime(Math.min(9000, f * 3), t0 + 0.01, 0.35 * pitchFactor);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(level, t0 + spec.attack);
    env.gain.setTargetAtTime(level * 0.0001, t0 + spec.attack, tau);

    const mix = ctx.createGain();
    mix.gain.value = 0.5;
    osc.connect(mix);
    osc2.connect(mix);
    mix.connect(filter);
    filter.connect(env);
    env.connect(this.dry);
    env.connect(this.wet);

    osc.start(t0);
    osc2.start(t0);
    let stopped = false;
    const stop = (at?: number) => {
      if (stopped) return;
      stopped = true;
      const t = Math.max(at ?? ctx.currentTime, ctx.currentTime);
      env.gain.cancelScheduledValues(t);
      env.gain.setTargetAtTime(0, t, spec.release / 4);
      try {
        osc.stop(t + spec.release * 2);
        osc2.stop(t + spec.release * 2);
      } catch {
        /* already stopped */
      }
    };
    // Written duration ends: dampers fall.
    const off = t0 + Math.max(0.05, dur);
    env.gain.setTargetAtTime(0, off, spec.release / 4);
    osc.stop(off + spec.release * 2);
    osc2.stop(off + spec.release * 2);
    osc.onended = () => {
      stopped = true;
      mix.disconnect();
      filter.disconnect();
      env.disconnect();
    };
    return { stop, startTime: t0 };
  }
}

let shared: Synth | null = null;
let preferredTimbre: Timbre = 'piano';

/** One shared synth; created on first use, which should follow a user gesture. */
export function getSynth(): Synth {
  if (!shared) {
    shared = new Synth();
    shared.timbre = preferredTimbre;
  }
  return shared;
}

/** Choose the instrument without creating an audio context. */
export function setTimbre(t: Timbre): void {
  preferredTimbre = t;
  if (shared) shared.timbre = t;
}

export function audioSupported(): boolean {
  return typeof window !== 'undefined' && !!(window.AudioContext ?? (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext);
}
